import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toLocaleString()}`;

function ReturnControls({ op, onSaved }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState("");
  const save = async () => {
    try {
      const res = await axios.put(`${API}/overpayments/${op.id}`, { refund_sent_date: sent });
      onSaved(res.data);
      toast.success(`Overpayment ${op.claim_id} marked returned ${sent}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  if (!open) return <button className="vr-link" onClick={() => setOpen(true)} data-testid={`op-return-${op.claim_id}`}>Record refund sent…</button>;
  return (
    <div className="vr-form">
      <input className="inp" type="date" value={sent} onChange={(e) => setSent(e.target.value)} data-testid={`op-return-date-${op.claim_id}`} />
      <div className="vr-btnrow">
        <button className="btn" onClick={save} disabled={!sent} data-testid={`op-return-save-${op.claim_id}`}>Mark returned</button>
        <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function OverpaymentLog({ active }) {
  const [rows, setRows] = useState(null);
  const [claimId, setClaimId] = useState("");
  const [payer, setPayer] = useState("");
  const [amount, setAmount] = useState("");
  const [identified, setIdentified] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchRows = useCallback(() => {
    axios.get(`${API}/overpayments`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { if (active === "op") fetchRows(); }, [active, fetchRows]);

  const canSubmit = claimId.trim() && Number(amount) > 0 && identified && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/overpayments`, { claim_id: claimId, payer, amount: Number(amount), identified_date: identified, note });
      toast.success(`Overpayment logged — 60-day clock runs to ${res.data.deadline}`);
      setClaimId(""); setPayer(""); setAmount(""); setIdentified(""); setNote("");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log overpayment");
    }
    setSubmitting(false);
  };

  const onSaved = (d) => setRows((p) => p.map((x) => (x.id === d.id ? d : x)));
  const openCount = (rows || []).filter((r) => r.status !== "RETURNED").length;

  return (
    <section className={`chapter utility${active === "op" ? " active" : ""}`} id="op">
      <div className="eyebrow">Working record · executes SOP 4.3 — an identified overpayment is returned within 60 calendar days (ACA §6402)</div>
      <h1>Overpayment 60-Day Log</h1>
      <p className="lede">The day an overpayment is identified, the statutory clock starts: 60 calendar days to report and return it, or the claim converts to reverse-False-Claims-Act exposure. This log runs the clock, and the reminders bar escalates at 14 days out and at overdue.</p>

      <div className="formcard" data-testid="op-form">
        <div className="formcard-title">Log an identified overpayment</div>
        <div className="fld-grid three">
          <div className="fld"><label>Claim / case ID</label><input className="inp" value={claimId} onChange={(e) => setClaimId(e.target.value)} placeholder="Claim reference" data-testid="op-claimid" /></div>
          <div className="fld"><label>Payer</label><input className="inp" value={payer} onChange={(e) => setPayer(e.target.value)} placeholder="Payer name" data-testid="op-payer" /></div>
          <div className="fld"><label>Amount ($)</label><input className="inp" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" data-testid="op-amount" /></div>
        </div>
        <div className="fld-grid">
          <div className="fld"><label>Date identified (starts the 60-day clock)</label><input className="inp" type="date" value={identified} onChange={(e) => setIdentified(e.target.value)} data-testid="op-identified" /></div>
          <div className="fld"><label>Note</label><input className="inp" value={note} onChange={(e) => setNote(e.target.value)} placeholder="How it was identified, refund route…" data-testid="op-note" /></div>
        </div>
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="op-submit">Start the 60-day clock</button>
      </div>

      <h2>Register {rows ? `— ${openCount} on the clock` : ""}</h2>
      <table data-testid="op-table">
        <tbody>
          <tr><th>Claim</th><th>Payer</th><th>Amount</th><th>Identified</th><th>60-day deadline</th><th>Days left</th><th style={{ width: 220 }}>Status</th></tr>
          {rows === null ? (
            <tr><td colSpan={7}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={7}>No overpayments on the log.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td className="q">{r.claim_id}</td>
                <td>{r.payer || "—"}</td>
                <td>{money(r.amount)}</td>
                <td>{r.identified_date}</td>
                <td>{r.deadline}</td>
                <td>{r.days_left != null ? r.days_left : "—"}</td>
                <td>
                  {r.status === "RETURNED" ? (
                    <span className="vr-pill cleared" data-testid={`op-status-${r.claim_id}`}>RETURNED {r.refund_sent_date}</span>
                  ) : (
                    <>
                      <span className={`vr-pill ${r.status === "OVERDUE" ? "declined" : "open"}`} data-testid={`op-status-${r.claim_id}`}>{r.status}</span>
                      <ReturnControls op={r} onSaved={onSaved} />
                    </>
                  )}
                  {r.note ? <div className="cap-sub">{r.note}</div> : null}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Retention: 10 years with claim records. A missed 60-day deadline is reported to counsel the same day (SOP 4.3 / 18.1).</p>
    </section>
  );
}
